import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Screen, computed, createSelectors } from '@trg/utils';

export interface LayoutState {
  screen: Screen;
  headerHeight: number;
  isFavoriting: boolean;
  isAddingToCart: boolean;
  lastFavoritingTimer: NodeJS.Timeout | null;
  lastAddingToCartTimer: NodeJS.Timeout | null;
}

export interface LayoutAction {
  setScreen(screen: Screen): void;
  setHeaderHeight(height: number): void;
  triggerFavoriting(duration?: number): void;
  triggerAddingToCart(duration?: number): void;
}

export type LayoutStore = LayoutState & LayoutAction;

export interface ComputedLayoutState {
  isMobile: boolean;
  isPad: boolean;
  isDesktop: boolean;
}

const computeLayoutState = (state: LayoutStore): ComputedLayoutState => ({
  isMobile: state.screen <= Screen.sm,
  isPad: state.screen === Screen.md,
  isDesktop: state.screen >= Screen.lg,
});

const useLayoutStoreBase = create<LayoutStore>()(
  devtools(
    computed(
      (set, get) => ({
        screen: Screen.md,
        headerHeight: 0,
        isFavoriting: false,
        isAddingToCart: false,
        lastFavoritingTimer: null,
        lastAddingToCartTimer: null,
        setScreen(screen) {
          set({ screen });
        },
        setHeaderHeight(height) {
          set({ headerHeight: height });
        },
        triggerFavoriting(duration = 1500) {
          const { lastFavoritingTimer } = get();
          lastFavoritingTimer && clearTimeout(lastFavoritingTimer);
          set({
            isFavoriting: true,
            lastFavoritingTimer: setTimeout(() => {
              set({ isFavoriting: false });
            }, duration),
          });
        },
        triggerAddingToCart(duration = 1500) {
          const { lastAddingToCartTimer } = get();
          lastAddingToCartTimer && clearTimeout(lastAddingToCartTimer);
          set({
            isAddingToCart: true,
            lastAddingToCartTimer: setTimeout(() => {
              set({ isAddingToCart: false });
            }, duration),
          });
        },
      }),
      computeLayoutState
    ),
    {
      name: 'layout',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
);

export const useLayoutStore = createSelectors(useLayoutStoreBase);
